<template>
  <b-container class="mt-5">
    <div v-if="eventDetail">
      <div
        v-if="imageResized"
        class="resized-image"
        @click="resizeImage"
      >
        <b-img
          :src="getImage(eventDetail)"
          class="card-img-top"
          rounded
        />
        <b-icon
          icon="x"
          class="close"
        />
      </div>
      <b-modal
        v-model="showDeleteAlert"
        title="Confirmation!"
        size="lg"
        ok-variant="danger"
        ok-title="Delete!"
        @ok="deleteEvent"
      >
        <h5 class="text-danger text-center">
          Are you sure you want to delete this event?
        </h5>
      </b-modal>

      <b-row>
        <b-col>
          <h1 class="font-weight-light">
            {{ eventDetail.title }}
          </h1>
        </b-col>
      </b-row>

      <b-row>
        <b-breadcrumb>
          <b-breadcrumb-item :to="{ name: 'events' }">
            <b-icon
              icon="house-fill"
              scale="1.25"
              shift-v="1.25"
              aria-hidden="true"
            />
            Events
          </b-breadcrumb-item>
          <b-breadcrumb-item active>
            {{ eventDetail.title }}
          </b-breadcrumb-item>
        </b-breadcrumb>
      </b-row>

      <b-row class="mt-2">
        <b-col
          class="cover-image"
          @click="resizeImage"
        >
          <b-icon
            icon="fullscreen"
            class="expand-icon"
          />
          <div
            v-if="!eventDetail.published"
            class="draft-badge"
          >
            <h3>
              <b-badge variant="warning">
                下書き
              </b-badge>
            </h3>
          </div>
          <b-img
            :src="getImage(eventDetail)"
            class="single-picture card-img-top"
            rounded
            fluid-grow
          />
          <b-img
            :src="getImage(eventDetail)"
            class="blurred card-img-top"
            rounded
            fluid-grow
          />
        </b-col>
      </b-row>

      <b-row class="mt-4">
        <b-col
          cols="12"
          sm="3"
        >
          <p>
            <b-icon icon="calendar2-event" />
            {{ getDate(eventDetail.date) }}
          </p>
        </b-col>
        <b-col
          cols="12"
          sm="9"
          class="d-flex justify-content-end"
        >
          <span class="text-monospace text-overflow">
            <b-icon
              icon="map"
              class="mr-1"
            />
            <span>{{ eventDetail.location }}</span>
          </span>
        </b-col>
      </b-row>

      <b-row class="mt-1">
        <b-col
          cols="12"
          sm="9"
        >
          <div v-if="eventDetail.url">
            <b-icon
              icon="link"
              class="mr-1"
            />
            <a
              :href="eventUrl"
              target="_blank"
            >{{ decodeURI(eventUrl) }}</a>
          </div>
        </b-col>
        <b-col
          v-if="eventDetail.mapUrl"
          cols="12"
          sm="3"
          class="d-flex justify-content-end align-items-center"
        >
          <b-icon
            icon="geo"
            class="mr-1"
          />
          <a
            class="map-url"
            :href="eventDetail.mapUrl"
            target="_blank"
          >
            Show on Google Maps
          </a>
        </b-col>
      </b-row>

      <b-row class="mt-3">
        <b-col>
          <p class="lead">
            <vue-simple-markdown :source="eventDetail.description" />
          </p>
        </b-col>
      </b-row>

      <b-row>
        <b-col>
          <div>
            <h5>
              <b-badge
                v-for="item in eventDetail.tags"
                :key="item.id"
                class="mr-2 pink"
              >
                {{ item.title }}
              </b-badge>
            </h5>
          </div>
        </b-col>
      </b-row>

      <b-row class="justify-content-center mt-5">
        <b-col
          cols="12"
          sm="6"
        >
          <b-form-group>
            <b-button
              :to="{name: 'event-edit', params: {id: eventDetail.id}}"
              block
              variant="primary"
              size="lg"
            >
              イベントを編集する
            </b-button>
          </b-form-group>
        </b-col>
        <b-col
          cols="12"
          sm="6"
        >
          <b-form-group>
            <b-button
              block
              variant="danger"
              size="lg"
              :disabled="eventStatus === 'loading'"
              @click="showDeleteAlert = true;"
            >
              <b-icon
                v-if="eventStatus === 'loading'"
                icon="three-dots"
                animation="cylon"
                font-scale="1"
              />
              <span v-else>イベントを削除する</span>
            </b-button>
          </b-form-group>
        </b-col>
      </b-row>
    </div>
    <Loader v-if="eventStatus === 'loading'" />
  </b-container>
</template>

<script>
import {mapGetters} from "vuex";
import {EVENT_DELETE, EVENT_DETAIL} from "@/store/constant";
import {getImage, getDate} from "@/helpers";
import Loader from "@/components/Loader";

export default {
  name: 'EventDetail',
  components: {Loader},
  data() {
    return {
      showDeleteAlert: false,
      imageResized: false,
    }
  },
  computed: {
    ...mapGetters(['eventDetail', 'eventStatus']),
    eventUrl() {
      const re = new RegExp("^(http|https)://", "i");
      const test = re.test(this.eventDetail.url);
      return test ?this.eventDetail.url :`https://${this.eventDetail.url}`;
    }
  },
  mounted() {
    this.eventId = this.$route.params.id;
    this.getEvent();
  },
  methods: {
    getEvent() {
      this.$store.dispatch(EVENT_DETAIL, this.eventId);
    },
    deleteEvent() {
      this.$store.dispatch(EVENT_DELETE, this.eventId)
        .then(() => {
          this.$router.push({ name: 'events' });
        });
    },
    resizeImage() {
      this.imageResized = !this.imageResized;
    },
    getImage,
    getDate,
  }
}
</script>

<style lang="less" scoped>
.cover-image {
  margin-top: 0.5rem;
  overflow: hidden;
  cursor: pointer;
  &:hover {
    .expand-icon {
      opacity: 1;
    }
  }
}
.expand-icon {
  position: absolute;
  top: 25px;
  right: 25px;
  color: #fff;
  font-size: 2.5rem;
  opacity: 0;
  transition: opacity .3s;
  background: #000;
  padding: 3px 5px;
  border-radius: 5px;
}
.resized-image {
  background: rgba(0, 0, 0, 0.8);
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  z-index: 1020;

  img {
    height: 100%;
    width: 100%;
    object-fit: contain;
  }

  .close {
    position: absolute;
    top: 30px;
    right: 30px;
    font-size: 4.5rem;
    color: #fff;
    cursor: pointer;
  }
}
</style>
